import React, { useEffect, useState, useRef } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Toolbar, Sort, Filter, Resize, RowDD, ContextMenu, VirtualScroll } from '@syncfusion/ej2-react-grids';
import type { ContextMenuClickEventArgs, ContextMenuItemModel } from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { useToast } from '../hooks/useToast';
import { useTime } from '../hooks/useTime';
import { CsvImportDialog } from './CsvImportDialog';
import { useEntityPermissions } from '../utils/permissions';
import { generateTempUUID } from '../utils/uuid';

export type GridColumnMeta = {
  field: string;
  headerKey: string;
  width?: number;
  type?: 'text' | 'number' | 'date';
  align?: 'Left' | 'Right' | 'Center';
  render?: (data: any) => React.ReactNode;
};

export type CrudFormProps<T> = {
  value: T | null;
  onClose: () => void;
  onSave: (value: T) => Promise<T | void> | T | void;
  entityName?: string; // Optional: Entity name for permission checking (e.g., "material", "user")
  isClone?: boolean; // Indicates if this is a clone operation
  materialType?: 'PRODUCT' | 'RAW_MATERIAL' | 'INTERMEDIATE_PRODUCT'; // Optional: Material type for MaterialFormDialog
  routingType?: 'PRODUCT_ROUTING' | 'INTERMEDIATE_ROUTING'; // Optional: Routing type for RoutingFormDialog
};

export type CrudPageProps<T, IdType = string> = {
  titleKey: string;
  gridMeta: GridColumnMeta[];
  fetchData: () => Promise<T[]>;
  saveData: (item: T) => Promise<T | void>;
  deleteData: (id: IdType) => Promise<void>;
  FormComponent: React.ComponentType<CrudFormProps<T>>;
  refreshKey?: string | number;
  localMode?: boolean; // When true, don't reload data after save/delete operations
  customToolbarButtons?: Array<{
    text: string;
    tooltipText?: string;
    prefixIcon?: string;
    id: string;
    disabled?: boolean;
    onClick?: (selectedItems: T[]) => void;
  }>;
  getCustomToolbarButtons?: (selectedItems: T[]) => Array<{
    text: string;
    tooltipText?: string;
    prefixIcon?: string;
    id: string;
    disabled?: boolean;
    onClick?: (selectedItems: T[]) => void;
  }>;
  showDefaultToolbar?: boolean;
  showAddButton?: boolean;
  showEditButton?: boolean;
  showCloneButton?: boolean;
  showDeleteButton?: boolean;
  showSearchButton?: boolean;
  allowFiltering?: boolean;
  allowSorting?: boolean;
  showTitle?: boolean;
  noPadding?: boolean;
  showSummary?: boolean;
  gridHeight?: string;
  // New priority-related props
  hasPriority?: boolean;
  priorityField?: string;
  onPriorityChange?: (items: T[]) => Promise<void>;
  // Custom handlers for toolbar actions (override default behavior)
  onAdd?: (selectedItems: T[]) => T | void;
  onEdit?: (selectedItems: T[]) => void;
  onDelete?: (selectedItems: T[]) => void;
  // New import-related props
  showImportButton?: boolean;
  onImportData?: (data: T[], options?: { isBatchItem?: boolean }) => Promise<void>;
  importEntityType?: string;
  entityFields?: Array<{
    field: string;
    label: string;
    type: 'text' | 'number' | 'date' | 'enum' | 'boolean';
    required?: boolean;
    enumValues?: string[];
    defaultValue?: any;
  }>;
  entityName?: string;
  importOptions?: {
    allowPartialImport?: boolean;
    batchSize?: number;
    validateBeforeImport?: boolean;
    showDetailedErrors?: boolean;
    autoRollbackOnFailure?: boolean;
    /** false = save directly (for example DemandsPage). true = only on 'Next' (wizard step 1). */
    deferredSave?: boolean;
  };
  // Context menu props
  showContextMenu?: boolean;
  contextMenuItems?: Array<{
    id: string;
    text: string;
    iconCss?: string;
  }>;
  onContextMenuClick?: (args: { item: { id: string; text: string }; rowInfo?: { rowData: T; rowIndex: number } }) => void;
  /** Extra props for FormComponent, spread before the core props so they can never override value/onClose/onSave. Keep FormComponent a stable (module-level) component and pass per-render data here; an inline arrow would remount the form on every render. */
  formProps?: Partial<CrudFormProps<T>> & Record<string, unknown>;
  suppressDeleteToast?: boolean;
  /** 'Single' = only one entry can be deleted at a time (single selection); 'Multiple' = standard multi delete */
  deletionMode?: 'Single' | 'Multiple';
  /** Incremental update without a full refresh (for example on SSE): the patch is applied to the affected item */
  incrementalUpdate?: { id: IdType; patch: Partial<T> } | null;
  /** Called after incrementalUpdate has been applied (the parent can reset its state) */
  onIncrementalUpdateApplied?: () => void;
  /** Enable pagination (recommended with many template columns - reduces the render time considerably) */
  allowPaging?: boolean;
  /** Number of rows per page when paginating (default: 25) */
  pageSize?: number;
  /** Virtual scrolling instead of pagination: renders only the visible rows (faster than paging for large data sets) */
  enableVirtualization?: boolean;
};

// Helper function to get nested object values
const getValue = (data: any, field: string) => {
  return field.split('.').reduce((obj, key) => obj?.[key], data);
};

export function CrudPage<T extends { id: IdType }, IdType = string>({
  titleKey,
  gridMeta,
  fetchData,
  saveData,
  deleteData,
  FormComponent,
  customToolbarButtons,
  getCustomToolbarButtons,
  showDefaultToolbar = true,
  showAddButton,
  showEditButton,
  showCloneButton,
  showDeleteButton,
  showSearchButton = true,
  allowFiltering = true,
  allowSorting = true,
  showTitle = true,
  noPadding = false,
  showSummary = true,
  gridHeight = "100%",
  hasPriority = false,
  priorityField = 'priority',
  onPriorityChange,
  onAdd,
  onEdit,
  onDelete,
  showImportButton = false,
  onImportData,
  importEntityType,
  entityFields = [],
  entityName = 'Entity',
  importOptions = {},
  refreshKey,
  localMode = false,
  showContextMenu = false,
  contextMenuItems: customContextMenuItems,
  onContextMenuClick,
  formProps,
  suppressDeleteToast,
  deletionMode = 'Multiple',
  incrementalUpdate,
  onIncrementalUpdateApplied,
  allowPaging = false,
  pageSize = 25,
  enableVirtualization = false,
}: CrudPageProps<T, IdType>) {
  
  const { t } = useTranslation(['common']);
  const [items, setItems] = useState<T[]>([]);
  const [selected, setSelected] = useState<T[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [clonedItem, setClonedItem] = useState<T | null>(null);
  const [pendingNewItem, setPendingNewItem] = useState<T | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const gridRef = useRef<GridComponent>(null);
  
  // Use the new API error handler and toast
  const { handleError } = useApiErrorHandler();
  const { showToast } = useToast();

  // Get entity permissions for permission-based button visibility
  const entityPermissions = useEntityPermissions(entityName.toLowerCase());

  // Server/mock time for the last-update label (so DEV shows the mocked time, not the browser time)
  const { currentTime, refreshTime } = useTime();
  
  // Debug logging for permissions
  useEffect(() => {
  }, [entityName, entityPermissions, showAddButton, showEditButton, showDeleteButton, showCloneButton]);
  
  // Determine button visibility: use explicit props if provided, otherwise use permissions
  // If showAddButton is explicitly set to false, respect that; otherwise check permission
  const canCreate = showAddButton !== undefined ? showAddButton : entityPermissions.canCreate;
  const canEdit = showEditButton !== undefined ? showEditButton : entityPermissions.canEdit;
  const canDelete = showDeleteButton !== undefined ? showDeleteButton : entityPermissions.canDelete;
  // Clone requires create permission
  const canClone = showCloneButton !== undefined ? showCloneButton : entityPermissions.canCreate;

  // Use a ref to store the latest fetchData function to avoid dependency issues
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);
  
  // Track if this is the first render to avoid double-loading on mount
  const isFirstRender = useRef(true);
  
  // Initial load on mount
  useEffect(() => {
    load();
    // Only run on mount, not when fetchData changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh data when refreshKey changes
  // In localMode, don't reload from fetchData - items are managed by parent state
  useEffect(() => {
    // Skip on first render to avoid double-loading (initial load is handled by mount useEffect)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    if (refreshKey !== undefined && refreshKey !== null) {
      if (!localMode) {
        load();
      } else {
        // In localMode, fetchData should return current state, so we can still call it
        // but it should be fast since it's just reading from state
        load();
      }
    }
    // Only depend on refreshKey, not fetchData, to avoid infinite loops
    // fetchData should be stable (wrapped in useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, localMode]);

  // Incremental update (for example on SSE): apply the patch to the affected item, no full refresh
  useEffect(() => {
    if (!incrementalUpdate?.id || !incrementalUpdate?.patch) return;
    setItems(prev => prev.map(item =>
      (item as any).id === incrementalUpdate.id || (item as any).uuid === incrementalUpdate.id
        ? { ...item, ...incrementalUpdate.patch }
        : item
    ));
    onIncrementalUpdateApplied?.();
  }, [incrementalUpdate, onIncrementalUpdateApplied]);

  // Syncfusion EJ2 React: the dataSource prop is not applied to the instance reliably (the grid stays empty).
  // setProperties({ dataSource }) internally triggers renderModule.refresh - without a full refresh(), which collides with the
  // React wrapper (updateProperties/setProperties) in the same tick and causes Observer.off(undefined).
  useEffect(() => {
    const t = window.setTimeout(() => {
      const grid = gridRef.current as GridComponent & { isDestroyed?: boolean };
      if (!grid || grid.isDestroyed || !(grid as any).element) return;
      if ((grid as any).dataSource === items) return;
      grid.setProperties({ dataSource: items as any }, false);
    }, 0);
    return () => window.clearTimeout(t);
  }, [items]);

  const load = async () => {
    try {
      // Only clear items if we don't have any data yet - this prevents the list from jumping
      // when refreshing with existing data
      if (items.length === 0) {
        setItems([]); // Clear items first to show loading state (only if empty)
      }
      const data = await fetchDataRef.current();
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.warn('❌ CrudPage load() received non-array data:', data);
        setItems([]);
        return;
      }
      
      // Sort by priority field if it exists (even if hasPriority is false, we still want to sort by operationNumber)
      // For position-based sorting, null values should be last
      // Note: fetchData might already sort the data, but we ensure consistent sorting here
      if (priorityField && data.length > 0 && (data[0] as any)[priorityField] !== undefined) {
        data.sort((a, b) => {
          const aPriority = (a as any)[priorityField];
          const bPriority = (b as any)[priorityField];
          // Handle null values: null should be last
          if (aPriority == null && bPriority == null) {
            // Fallback to name if both are null
            const aName = (a as any).name || '';
            const bName = (b as any).name || '';
            return aName.localeCompare(bName);
          }
          if (aPriority == null) return 1;
          if (bPriority == null) return -1;
          const posCompare = aPriority - bPriority;
          if (posCompare !== 0) return posCompare;
          // If positions are equal, sort by name
          const aName = (a as any).name || '';
          const bName = (b as any).name || '';
          return aName.localeCompare(bName);
        });
      }
      
      setItems(data);
      // Update the last-update label to the server/mock time
      refreshTime();
    } catch (error: any) {
      console.error('CrudPage load error:', error);
      console.error('CrudPage load error details:', {
        message: error?.message,
        status: error?.status,
        response: error?.response,
        body: error?.body
      });
      console.error('CrudPage load error stack:', error?.stack);
      // Don't clear items on error - keep previous data visible
      await handleError(error, 'loading data');
    }
  };

  const handleAdd = () => {
    // If custom handler is provided, call it and then open dialog if it returns a value
    if (onAdd) {
      const result = onAdd(selected);
      // If onAdd returns a value, open dialog with that value
      if (result !== undefined && result !== null) {
        setSelected([]);
        setEditMode(false);
        setClonedItem(null);
        setPendingNewItem(result as T);
        // Open dialog immediately - no need for setTimeout
        setDialogOpen(true);
      }
      return;
    }
    setSelected([]);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleEdit = () => {
    // If custom handler is provided, use it instead
    if (onEdit) {
      onEdit(selected);
      return;
    }
    if (selected.length === 1) {
      setEditMode(true);
      setDialogOpen(true);
    }
  };

  const handleDelete = () => {
    // If custom handler is provided, use it instead
    if (onDelete) {
      onDelete(selected);
      return;
    }
    if (selected.length > 0) {
      // Show confirmation dialog
      setDeleteConfirmDialogOpen(true);
    }
  };

  const confirmDelete = async () => {
    setDeleteConfirmDialogOpen(false);
    if (selected.length > 0) {
      try {
        for (const item of selected) {
          await deleteData(item.id);
        }
        // Clear selection after delete
        setSelected([]);
        if (!suppressDeleteToast) {
          showToast('success', t('actions.delete'), t('messages.deleteSuccess'));
        }
        
        // Always reload data to refresh the grid, even in local mode
        // In local mode, fetchData should return the updated state from parent
        await load();
      } catch (error) {
        await handleError(error, 'deleting item');
      }
    }
  };

  // Deep clone helper function that removes IDs from nested objects/arrays
  // For routings, we need to generate new UUIDs for operations and update references
  const deepCloneWithoutIds = (obj: any, isRoot: boolean = true, operationUuidMap?: Map<string, string>): any => {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => deepCloneWithoutIds(item, false, operationUuidMap));
    }
    
    const cloned: any = {};
    
    // Special handling for operations: generate new UUIDs and create a map
    if (isRoot && obj.operations && Array.isArray(obj.operations)) {
      const newOperationUuidMap = new Map<string, string>();
      
      // First pass: clone operations and generate new UUIDs
      cloned.operations = obj.operations.map((op: any) => {
        
        const newUuid = op.uuid ? generateTempUUID() : undefined;
        if (op.uuid && newUuid) {
          newOperationUuidMap.set(op.uuid, newUuid);
        }
        const clonedOp = deepCloneWithoutIds(op, false, newOperationUuidMap);
        if (newUuid) {
          clonedOp.uuid = newUuid;
        }
        
        
        return clonedOp;
      });
      
      // Second pass: clone operation relationships and update references
      if (obj.operationRelationships && Array.isArray(obj.operationRelationships)) {
        
        cloned.operationRelationships = obj.operationRelationships.map((rel: any) => {
          const clonedRel = deepCloneWithoutIds(rel, false, newOperationUuidMap);
          // Update predecessorId and successorId to new operation UUIDs
          if (clonedRel.predecessorId && newOperationUuidMap.has(clonedRel.predecessorId)) {
            clonedRel.predecessorId = newOperationUuidMap.get(clonedRel.predecessorId);
          }
          if (clonedRel.successorId && newOperationUuidMap.has(clonedRel.successorId)) {
            clonedRel.successorId = newOperationUuidMap.get(clonedRel.successorId);
          }
          return clonedRel;
        });

      }

      // Clone resource requirements and update operation references
      // Use the same approach as operationRelationships: map to new temp UUIDs
      if (obj.resourceRequirements && Array.isArray(obj.resourceRequirements)) {
        cloned.resourceRequirements = obj.resourceRequirements.map((rr: any) => {
          const clonedRr = deepCloneWithoutIds(rr, false, newOperationUuidMap);
          
          // Both fromOperationId and toOperationId are required (NOT NULL in database)
          // Map them to the new operation temp UUIDs (same as operationRelationships)
          if (!clonedRr.fromOperationId) {
            console.error('🔍 deepCloneWithoutIds - fromOperationId is missing in resource requirement, skipping');
            return null;
          }
          
          if (!clonedRr.toOperationId) {
            console.error('🔍 deepCloneWithoutIds - toOperationId is missing in resource requirement, skipping');
            return null;
          }
          
          // Update fromOperationId to new operation temp UUID
          if (newOperationUuidMap.has(clonedRr.fromOperationId)) {
            clonedRr.fromOperationId = newOperationUuidMap.get(clonedRr.fromOperationId)!;
          } else {
            console.error('🔍 deepCloneWithoutIds - fromOperationId not found in UUID map:', clonedRr.fromOperationId);
            return null;
          }
          
          // Update toOperationId to new operation temp UUID
          if (newOperationUuidMap.has(clonedRr.toOperationId)) {
            clonedRr.toOperationId = newOperationUuidMap.get(clonedRr.toOperationId)!;
          } else {
            console.error('🔍 deepCloneWithoutIds - toOperationId not found in UUID map:', clonedRr.toOperationId);
            return null;
          }
          
          // Also update conditionalOperationId if present
          if (clonedRr.conditionalOperationId && newOperationUuidMap.has(clonedRr.conditionalOperationId)) {
            clonedRr.conditionalOperationId = newOperationUuidMap.get(clonedRr.conditionalOperationId)!;
          }
          
          return clonedRr;
        }).filter((rr: any) => rr !== null); // Remove any null entries (failed mappings)
        
      }
      
      // Clone shift assignments (for Resources)
      if (obj.shiftAssignments && Array.isArray(obj.shiftAssignments)) {
        cloned.shiftAssignments = obj.shiftAssignments.map((sa: any) => {
          const clonedSa = deepCloneWithoutIds(sa, false);
          // Remove UUID so backend generates a new one
          clonedSa.uuid = undefined;
          // Remove resourceId as it will be set to the new resource
          clonedSa.resourceId = undefined;
          return clonedSa;
        });
      }
      
      // Continue with other fields
      for (const key in obj) {
        if (key === 'id' || key === 'uuid' || key === 'operations' || key === 'operationRelationships' || key === 'resourceRequirements' || key === 'shiftAssignments') {
          continue; // Already handled
        }
        
        const value = obj[key];
        if (value !== null && typeof value === 'object') {
          cloned[key] = deepCloneWithoutIds(value, false, newOperationUuidMap);
        } else {
          cloned[key] = value;
        }
      }
      
      return cloned;
    }
    
    // Normal cloning for non-root objects
    for (const key in obj) {
      // Skip ID fields at root level, but also remove them from nested objects
      if (key === 'id' || key === 'uuid') {
        // Skip ID fields - but keep other fields
        continue;
      }
      
      // Special handling for shiftAssignments arrays (even in nested objects)
      if (key === 'shiftAssignments' && Array.isArray(obj[key])) {
        cloned[key] = obj[key].map((sa: any) => {
          const clonedSa = deepCloneWithoutIds(sa, false, operationUuidMap);
          // Remove UUID so backend generates a new one
          clonedSa.uuid = undefined;
          // Remove resourceId as it will be set to the new resource
          clonedSa.resourceId = undefined;
          return clonedSa;
        });
        continue;
      }
      
      const value = obj[key];
      if (value !== null && typeof value === 'object') {
        // Recursively clone nested objects and arrays
        cloned[key] = deepCloneWithoutIds(value, false, operationUuidMap);
      } else {
        // Copy primitive values
        cloned[key] = value;
      }
    }
    
    return cloned;
  };

  const handleClone = () => {
    if (selected.length === 1) {
      const itemToClone = selected[0] as any;
      
      // Deep clone the item and remove all IDs (including from nested objects/arrays)
      const clonedItem = deepCloneWithoutIds(itemToClone) as T;
      
      // Adjust the name to avoid UNIQUE KEY constraint violations
      if ((clonedItem as any).name) {
        // Append " - Kopie" to the name, or increment number if name ends with a number
        const originalName = (clonedItem as any).name;
        const copySuffix = ' - Kopie';
        
        // Check if name already ends with " - Kopie" or similar pattern
        if (originalName.endsWith(copySuffix)) {
          // If it already ends with " - Kopie", try to increment a number
          const match = originalName.match(/^(.+?)(\s*-\s*(\d+))?$/);
          if (match) {
            const baseName = match[1].replace(/\s*-\s*Kopie$/, '');
            const number = match[3] ? parseInt(match[3], 10) + 1 : 2;
            (clonedItem as any).name = `${baseName} - Kopie ${number}`;
          } else {
            (clonedItem as any).name = `${originalName} 2`;
          }
        } else {
          // Check if name ends with a number pattern (e.g., "Gassenhauer - 2 Ofenladung")
          const numberMatch = originalName.match(/^(.+?)\s*-\s*(\d+)\s*(.+)$/);
          if (numberMatch) {
            const baseName = numberMatch[1];
            const number = parseInt(numberMatch[2], 10) + 1;
            const suffix = numberMatch[3];
            (clonedItem as any).name = `${baseName} - ${number} ${suffix}`;
          } else {
            // Simply append " - Kopie"
            (clonedItem as any).name = `${originalName}${copySuffix}`;
          }
        }
      }
      // When cloning entities with default flag (e.g. campaign sequence), do not copy default
      if ((clonedItem as any).isDefault !== undefined) {
        (clonedItem as any).isDefault = false;
      }
      
      
      setSelected([]);
      setEditMode(false);
      setDialogOpen(true);
      // Pass the cloned item to the form
      setClonedItem(clonedItem);
    }
  };

  const handleImport = () => {
    setImportDialogOpen(true);
  };

  const handleImportData = async (data: T[], options?: { isBatchItem?: boolean }) => {
    if (!onImportData) {
      // Fallback to individual save if no bulk import handler
      for (const item of data) {
        await saveData(item);
      }
    } else {
      await onImportData(data, options);
    }
    // Reload data after import (no toast here – CSV import shows its own summary dialog)
    await load();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelected([]);
    setClonedItem(null);
  };


  const handleDialogSave = async (item: T): Promise<T | void> => {
    try {
      const savedItem = await saveData(item);
      const resolvedItem = (savedItem ?? item) as T;
      
      // Note: We don't show success toast here because some FormComponents (like UserRoleFormDialog)
      // perform additional operations (like saving permissions) after onSave returns.
      // Those components should show the success toast after all operations are complete.
      // If a FormComponent doesn't show its own toast, it should handle it in its onSave handler.
      
      // IMPORTANT: Delay closing the dialog to prevent "Node.removeChild" errors
      // Some FormComponents (like CampaignSequenceEntryDetailDialog) use visible={true}
      // and rely on CrudPage to control dialog visibility via dialogOpen state.
      // The delay allows the dialog to properly unmount before React tries to remove it.
      // If you encounter Node.removeChild errors with nested dialogs, ensure:
      // 1. FormComponent uses visible={true} (not a state variable)
      // 2. FormComponent's handleSave only calls onSave (doesn't close dialog itself)
      // 3. FormComponent's handleClose only calls onClose (doesn't close dialog itself)
      // 4. This setTimeout delay is present (150ms is usually sufficient)
      setTimeout(() => {
        setDialogOpen(false);
        setSelected([]);
        setClonedItem(null);
      }, 150);
      
      // Only reload data if not in local mode
      if (!localMode) {
        await load();
        
        // Add a small delay to ensure the grid has updated
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        setItems(prev => {
          const existingIndex = prev.findIndex(existing => existing.id === resolvedItem.id);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = resolvedItem;
            return updated;
          }
          return [...prev, resolvedItem];
        });
      }
      
      // Show success toast only if we're sure no additional operations will be performed
      // For now, we'll let FormComponents handle their own success toasts
      // This prevents showing success toast before permissions are saved (which could fail)
    } catch (error) {
      // Don't close dialog on error - let user fix the issue
      // handleError will show the error toast, so we don't need to show another one
      console.error('CrudPage handleDialogSave error:', error);
      await handleError(error, 'saving item');
      // Rethrow so FormComponent (e.g. wizard) knows save failed and does not close
      throw error;
    }
  };

  const selectionCount = selected.length;

  // Handle row reordering for priority-based entities
  const handleRowDrop = async (args: any) => {
    
    if (!hasPriority || !args.data) {
      return;
    }
    
    try {
      // Syncfusion Grid provides fromIndex and dropIndex (not toIndex) directly in args
      const fromIndex = args.fromIndex;
      const toIndex = args.dropIndex !== undefined ? args.dropIndex : args.toIndex;
      
      if (fromIndex === undefined || toIndex === undefined) {
        console.error('❌ CrudPage handleRowDrop: fromIndex or toIndex is undefined', { fromIndex, toIndex, args });
        return;
      }
      
      const reorderedItems = [...items];
      const movedItem = reorderedItems.splice(fromIndex, 1)[0];
      reorderedItems.splice(toIndex, 0, movedItem);
      
      
      // Update priority values based on new positions (1-based)
      const updatedItems = reorderedItems.map((item, index) => ({
        ...item,
        [priorityField]: index + 1
      }));
      
      
      // Call the priority change handler first to save to backend/parent state
      if (onPriorityChange) {
        await onPriorityChange(updatedItems);
      }
      
      // In localMode, update items state first, then call onPriorityChange
      // The parent component will update its state, and fetchData will return the updated data
      // In non-localMode, update local items state directly
      if (localMode) {
        // Update items state immediately to reflect the change in the UI
        setItems(updatedItems);
        // onPriorityChange will update the parent state, which will trigger fetchData to return updated data
        // But we don't need to call load() because the items are already updated
      } else {
        // Update local items state after successful save to reflect the change in the UI
        // This ensures the UI matches what was saved
        setItems(updatedItems);
      }
    } catch (error) {
      console.error('❌ CrudPage handleRowDrop: Error occurred:', error);
      await handleError(error, 'reordering items');
      // Reload data to restore original order
      await load();
    }
  };

  // Build toolbar buttons based on configuration and permissions
  // Buttons are always shown but disabled if user doesn't have permission
  const defaultToolbarButtons = showDefaultToolbar ? [
    // Show Add button if showAddButton is not explicitly false
    ...(showAddButton !== false ? [{
      text: t('actions.add'),
      tooltipText: canCreate ? t('actions.add') : t('actions.noPermission'),
      prefixIcon: 'e-add',
      id: 'add',
      disabled: !canCreate
    }] : []),
    // Show Edit button if showEditButton is not explicitly false
    ...(showEditButton !== false ? [{
      text: t('actions.edit'),
      tooltipText: canEdit ? t('actions.edit') : t('actions.noPermission'),
      prefixIcon: 'e-edit',
      id: 'edit',
      disabled: !canEdit || selectionCount !== 1
    }] : []),
    // Show Clone button if showCloneButton is not explicitly false
    ...(showCloneButton !== false ? [{
      text: t('actions.clone'),
      tooltipText: canClone ? t('actions.clone') : t('actions.noPermission'),
      prefixIcon: 'e-copy',
      id: 'clone',
      disabled: !canClone || selectionCount !== 1
    }] : []),
    // Show Delete button if showDeleteButton is not explicitly false
    ...(showDeleteButton !== false ? [{
      text: t('actions.delete'),
      tooltipText: canDelete ? t('actions.delete') : t('actions.noPermission'),
      prefixIcon: 'e-delete',
      id: 'delete',
      disabled: !canDelete || selectionCount < 1
    }] : []),
    ...(showImportButton ? [{
      text: t('actions.import'),
      tooltipText: t('actions.import'),
      prefixIcon: 'e-upload',
      id: 'import',
      disabled: false
    }] : []),
    ...(showSearchButton ? ['Search'] : [])
  ] : [];

  // Get dynamic custom toolbar buttons if function is provided, otherwise use static ones
  const dynamicCustomButtons = getCustomToolbarButtons ? getCustomToolbarButtons(selected) : [];
  const staticCustomButtons = customToolbarButtons || [];
  
  // Filter out default buttons that are overridden by custom buttons with same ID
  // Also filter out disabled custom buttons without onClick (they are used to hide default buttons)
  const customButtonIds = new Set(
    dynamicCustomButtons
      .filter(btn => !btn.disabled || btn.onClick) // Only consider enabled buttons or disabled buttons with onClick as replacements
      .map(btn => btn.id)
  );
  staticCustomButtons.forEach(btn => customButtonIds.add(btn.id));
  
  const filteredDefaultButtons = defaultToolbarButtons.filter(btn => {
    if (typeof btn === 'string') {
      return true;
    }
    return !customButtonIds.has(btn.id);
  });
  
  // Filter out disabled custom buttons without onClick (they are used to hide default buttons)
  const enabledCustomButtons = [
    ...dynamicCustomButtons.filter(btn => !btn.disabled || btn.onClick),
    ...staticCustomButtons
  ];
  
  // Combine default and custom toolbar buttons
  const toolbarButtons = [
    ...filteredDefaultButtons,
    ...enabledCustomButtons
  ];

  const toolbarClick = (args: any) => {
    if (!args.item) return;
    
    // Handle custom toolbar actions FIRST (both static and dynamic)
    // This ensures custom buttons take precedence over default actions
    const allCustomButtons = [
      ...(customToolbarButtons || []),
      ...(getCustomToolbarButtons ? getCustomToolbarButtons(selected) : [])
    ];
    
    const customButton = allCustomButtons.find(btn => btn.id === args.item.id || btn.text === args.item.text);
    if (customButton && customButton.onClick) {
      customButton.onClick(selected);
      return; // Don't process default actions if custom button was found
    }
    
    // Handle default toolbar actions only if no custom button was found
    switch (args.item.text) {
      case t('actions.add'): handleAdd(); break;
      case t('actions.edit'): handleEdit(); break;
      case t('actions.clone'): handleClone(); break;
      case t('actions.delete'): handleDelete(); break;
      case t('actions.import'): 
        // Only handle default import if showImportButton is true
        if (showImportButton) {
          handleImport();
        }
        break;
      case t('actions.createPlan'): handleAdd(); break; // Create Plan acts as Add
    }
  };

  // Use getSelectedRecords() so selection matches the displayed (sorted/filtered) rows,
  // not the original data order. getSelectedRowIndexes() returns visual indexes, and
  // mapping with items[idx] would open the wrong record after sorting.
  const handleRowSelected = (_e: any) => {
    if (!gridRef.current) {
      setSelected([]);
      return;
    }
    try {
      const records = gridRef.current.getSelectedRecords?.() ?? [];
      setSelected((records as T[]).filter(Boolean));
    } catch (error) {
      console.warn('Error getting selected rows:', error);
      setSelected([]);
    }
  };

  const handleRowDeselected = (_e: any) => {
    if (!gridRef.current) {
      setSelected([]);
      return;
    }
    try {
      const records = gridRef.current.getSelectedRecords?.() ?? [];
      setSelected((records as T[]).filter(Boolean));
    } catch (error) {
      console.warn('Error getting selected rows:', error);
      setSelected([]);
    }
  };

  // Reset selection when data changes
  const handleDataBound = () => {
    setSelected([]);
  };


  return (
    <div className={`h-full flex flex-col ${noPadding ? '' : 'p-4'}`} data-testid={`crud-page-${entityName?.toLowerCase() || 'default'}`}>
      {showTitle && <h4 className="text-xl font-bold mb-4">{t(titleKey)}</h4>}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0" data-testid="crud-grid-wrapper">
          <GridComponent
            dataSource={items}
            height={gridHeight}
            allowPaging={allowPaging && !enableVirtualization}
            enableVirtualization={enableVirtualization}
            pageSettings={enableVirtualization ? { pageSize: 50 } : allowPaging ? { pageSize } : undefined}
            allowSorting={allowSorting}
            allowFiltering={allowFiltering}
            allowResizing={true}
            selectionSettings={{ type: deletionMode === 'Single' ? 'Single' : 'Multiple' }}
            rowSelected={handleRowSelected}
            rowDeselected={handleRowDeselected}
            dataBound={handleDataBound}
            toolbar={toolbarButtons}
            toolbarClick={toolbarClick}
            locale="de-DE"
            ref={gridRef}
            allowRowDragAndDrop={hasPriority}
            rowDrop={(args: any) => {
              handleRowDrop(args);
            }}
            rowDragStart={(_args: any) => {
            }}
            contextMenuItems={showContextMenu ? (customContextMenuItems 
              ? customContextMenuItems.map(item => ({ ...item, target: '.e-content' }))
              : [
                { text: t('actions.add'), target: '.e-content', id: 'add', iconCss: 'e-icons e-add' },
                { text: t('actions.edit'), target: '.e-content', id: 'edit', iconCss: 'e-icons e-edit' },
                { text: t('actions.delete'), target: '.e-content', id: 'delete', iconCss: 'e-icons e-delete' }
              ]) as ContextMenuItemModel[] : undefined}
            contextMenuClick={showContextMenu ? (args: ContextMenuClickEventArgs) => {
              if (onContextMenuClick && args.item) {
                const rowInfo = args.rowInfo ? {
                  rowData: args.rowInfo.rowData as T,
                  rowIndex: args.rowInfo.rowIndex as number
                } : undefined;
                onContextMenuClick({
                  item: { id: args.item.id || '', text: args.item.text || '' },
                  rowInfo
                });
              } else if (args.item) {
                // Default behavior if no custom handler
                switch (args.item.id) {
                  case 'add':
                    if (onAdd) {
                      onAdd(selected);
                    } else {
                      setEditMode(false);
                      setDialogOpen(true);
                    }
                    break;
                  case 'edit':
                    if (args.rowInfo?.rowData) {
                      if (onEdit) {
                        onEdit([args.rowInfo.rowData as T]);
                      } else {
                        setSelected([args.rowInfo.rowData as T]);
                        setEditMode(true);
                        setDialogOpen(true);
                      }
                    }
                    break;
                  case 'delete':
                    if (args.rowInfo?.rowData) {
                      if (onDelete) {
                        onDelete([args.rowInfo.rowData as T]);
                      } else {
                        setSelected([args.rowInfo.rowData as T]);
                        setDeleteConfirmDialogOpen(true);
                      }
                    }
                    break;
                }
              }
            } : undefined}
          >
          <ColumnsDirective>
            {gridMeta.map((col, idx) => {
              // Special handling for shiftAssignments translation
              let headerText = t(col.headerKey);
              if (col.headerKey === 'grid.shiftAssignments' && headerText === 'grid.shiftAssignments') {
                // If translation not found, try direct lookup
                headerText = t('grid.shiftAssignments', 'Schichtzuweisungen');
              }
              return (
                <ColumnDirective
                  key={idx}
                  field={col.field}
                  headerText={headerText}
                  width={col.width}
                  textAlign={col.align || 'Left'}
                  valueAccessor={(field, data) => col.render ? undefined : getValue(data, field)}
                  template={col.render ? (col.render as any) : undefined}
                />
              );
            })}
          </ColumnsDirective>
          <Inject services={[Page, Toolbar, Resize, ...(allowSorting ? [Sort] : []), ...(allowFiltering ? [Filter] : []), ...(hasPriority ? [RowDD] : []), ...(showContextMenu ? [ContextMenu] : []), ...(enableVirtualization ? [VirtualScroll] : [])]} />
        </GridComponent>
        </div>
        
        {/* Custom footer with summary information */}
        {showSummary && (
          <div className="flex justify-between items-center p-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 mt-2">
            <div className="flex items-center space-x-4">
              <span>
                <strong>{items.length}</strong> {items.length === 1 ? 'Eintrag' : 'Einträge'} gesamt
              </span>
              {selected.length > 0 && (
                <span className="text-blue-600">
                  <strong>{selected.length}</strong> {selected.length === 1 ? 'ausgewählt' : 'ausgewählt'}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500" data-testid="last-update-time">
              Letzte Aktualisierung: {currentTime.toLocaleTimeString('de-DE')}
            </div>
          </div>
        )}
      </div>

      {dialogOpen && (
        <FormComponent
          {...formProps}
          value={pendingNewItem || clonedItem || (editMode && selected.length === 1 ? selected[0] : null)}
          onClose={() => {
            setPendingNewItem(null);
            handleDialogClose();
          }}
          onSave={handleDialogSave}
          entityName={entityName.toLowerCase()}
          isClone={!!clonedItem}
        />
      )}

      {importDialogOpen && (
        <CsvImportDialog<T>
          isOpen={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImport={handleImportData}
          entityFields={entityFields}
          entityName={entityName}
          entityType={importEntityType}
          importOptions={importOptions}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmDialogOpen && (
        <DialogComponent
          visible={deleteConfirmDialogOpen}
          isModal={true}
          header={t('actions.delete')}
          showCloseIcon={true}
          closeOnEscape={true}
          width="400px"
          close={() => setDeleteConfirmDialogOpen(false)}
          animationSettings={{ effect: 'None' }}
          footerTemplate={() => (
            <div className="flex justify-end gap-2">
              <ButtonComponent 
                onClick={() => setDeleteConfirmDialogOpen(false)} 
                cssClass="e-outline e-btn e-outline"
                type="button"
                data-testid="delete-cancel"
              >
                {t('actions.cancel')}
              </ButtonComponent>
              <ButtonComponent 
                onClick={confirmDelete} 
                cssClass="e-primary e-btn e-primary"
                type="button"
                data-testid="delete-confirm"
              >
                {t('actions.delete')}
              </ButtonComponent>
            </div>
          )}
        >
          <div className="p-4">
            <p>
              {t('messages.deleteConfirm', { count: selected.length, defaultValue: `Wollen Sie wirklich ${selected.length} ${selected.length === 1 ? 'Datensatz' : 'Datensätze'} löschen?` })}
            </p>
          </div>
        </DialogComponent>
      )}
    </div>
  );
}
